import React, { useState, useEffect } from "react";
import axios from "axios";
import { server } from "../../server";
import useAuth from "../../useAuth";
import {
  FaLayerGroup,
  FaPlus,
  FaTrash,
  FaTimes,
  FaSearch,
} from "react-icons/fa";
import { toast } from "react-toastify";

export default function FeeCategoriesPage() {
  const [feeCategories, setFeeCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const { token } = useAuth();

  // Category Modal State
  const [isCatModalOpen, setIsCatModalOpen] = useState(false);
  const [catForm, setCatForm] = useState({ name: "", description: "" });
  const [submittingCat, setSubmittingCat] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const catRes = await axios.get(`${server}/fee-enterprise/categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFeeCategories(catRes.data.data || []);
    } catch (error) {
      toast.error("Failed to load fee categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchData();
  }, [token]);

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!catForm.name) return toast.error("Category Name is required!");

    try {
      setSubmittingCat(true);
      const res = await axios.post(`${server}/fee-enterprise/categories`, catForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        toast.success("Fee Category Created Successfully!");
        setIsCatModalOpen(false);
        setCatForm({ name: "", description: "" });
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create fee category");
    } finally {
      setSubmittingCat(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm("Are you sure you want to delete this Fee Category?")) return;
    try {
      await axios.delete(`${server}/fee-enterprise/categories/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Fee Category deleted.");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete fee category");
    }
  };

  const filteredCategories = feeCategories.filter(
    (c) =>
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-4 max-w-[1400px] mx-auto min-h-screen bg-gray-50 dark:bg-slate-950 text-gray-800 dark:text-slate-100 font-sans transition-colors duration-300">
      {/* Compact Top Header Bar */}
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl px-5 py-3 mb-4 shadow-xs flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-xl shadow-xs">
            <FaLayerGroup size={18} />
          </div>
          <div>
            <h1 className="text-lg font-black text-gray-900 dark:text-white leading-tight">Fee Categories Master</h1>
            <p className="text-xs text-gray-500 dark:text-slate-400 font-semibold">Define category groups for organizing school fee structures</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsCatModalOpen(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3.5 py-1.5 rounded-xl font-black text-xs transition shadow-xs flex items-center gap-1.5"
          >
            <FaPlus size={10} /> Add Fee Category
          </button>
        </div>
      </div>

      {/* Search Toolbar */}
      <div className="bg-white dark:bg-slate-900 p-3 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="text-xs font-black text-gray-700 dark:text-slate-300">
          Fee Categories List ({filteredCategories.length})
        </div>

        {/* Live Search Bar */}
        <div className="relative w-full sm:w-64">
          <FaSearch className="absolute left-3 top-2.5 text-gray-400 dark:text-slate-500 text-xs" />
          <input
            type="text"
            placeholder="Search Fee Categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-900 dark:text-white outline-none"
          />
        </div>
      </div>

      {/* High-Density Table Display */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs overflow-hidden mb-8">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-800/80 text-gray-500 dark:text-slate-400 font-black text-[10px] uppercase border-b border-gray-200 dark:border-slate-800">
                <th className="p-2.5">Category Name</th>
                <th className="p-2.5">Description</th>
                <th className="p-2.5 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
              {loading ? (
                <tr>
                  <td colSpan="3" className="text-center py-12 text-indigo-600 font-bold">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                    Loading fee categories...
                  </td>
                </tr>
              ) : filteredCategories.length === 0 ? (
                <tr>
                  <td colSpan="3" className="text-center py-12 text-gray-400 italic">
                    No fee categories configured yet.
                  </td>
                </tr>
              ) : (
                filteredCategories.map((cat) => (
                  <tr key={cat.id} className="hover:bg-gray-50 dark:hover:bg-slate-800/40 transition">
                    <td className="p-2.5 font-black text-gray-900 dark:text-white">{cat.name}</td>
                    <td className="p-2.5 text-gray-500 dark:text-slate-400 font-medium">{cat.description || "N/A"}</td>
                    <td className="p-2.5 text-center">
                      <button
                        onClick={() => handleDeleteCategory(cat.id)}
                        className="p-1.5 text-rose-600 bg-rose-50 dark:bg-rose-950 rounded-lg hover:bg-rose-100 transition"
                        title="Delete Category"
                      >
                        <FaTrash size={11} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* CREATE CATEGORY MODAL */}
      {isCatModalOpen && (
        <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs z-50 flex justify-center items-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-md w-full shadow-2xl border border-gray-200 dark:border-slate-800 space-y-4">
            <div className="flex justify-between items-center border-b border-gray-100 dark:border-slate-800 pb-3">
              <h3 className="text-base font-black text-gray-900 dark:text-white flex items-center gap-2">
                <FaLayerGroup className="text-indigo-500" /> Add Fee Category
              </h3>
              <button onClick={() => setIsCatModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleCreateCategory} className="space-y-3">
              <div>
                <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1">
                  Category Group Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Academic Fees"
                  value={catForm.name}
                  onChange={(e) => setCatForm({ ...catForm, name: e.target.value })}
                  className="w-full p-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold text-gray-900 dark:text-white outline-none"
                />
              </div>

              <div>
                <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  placeholder="e.g. Group containing Academic fee structures"
                  value={catForm.description}
                  onChange={(e) => setCatForm({ ...catForm, description: e.target.value })}
                  className="w-full p-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-medium text-gray-900 dark:text-white outline-none"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-gray-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsCatModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-bold text-gray-600 dark:text-slate-400 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingCat}
                  className="px-5 py-2 bg-indigo-600 text-white text-xs font-black rounded-xl hover:bg-indigo-700 transition shadow-md"
                >
                  {submittingCat ? "Saving..." : "Save Category"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
